import { z } from 'zod';
import { isoDate, paging, phone, trimmed, uuid } from './common.js';

export const visitKind = z.enum(['guest', 'delivery', 'cab', 'service', 'other']);
export const visitStatus = z.enum([
  'pending',
  'approved',
  'rejected',
  'no_response',
  'entered',
  'exited',
  'cancelled',
  'expired',
]);
export type VisitStatus = z.infer<typeof visitStatus>;

export const visitDto = z.object({
  id: uuid,
  unitId: uuid,
  unitLabel: z.string(),
  kind: visitKind,
  source: z.enum(['walk_in', 'pre_approved']),
  status: visitStatus,
  visitorName: z.string(),
  visitorPhone: z.string().nullable(),
  company: z.string().nullable(),
  vehicleNo: z.string().nullable(),
  purpose: z.string().nullable(),
  decisionNote: z.string().nullable(),
  decidedByName: z.string().nullable(),
  decisionDeadline: isoDate.nullable(),
  createdAt: isoDate,
  decidedAt: isoDate.nullable(),
  enteredAt: isoDate.nullable(),
  exitedAt: isoDate.nullable(),
});
export type VisitDto = z.infer<typeof visitDto>;

/** Guard logs a walk-in. `idempotencyKey` lets an offline client retry safely. */
export const createVisit = z.object({
  unitId: uuid,
  kind: visitKind.default('guest'),
  visitorName: trimmed(1, 80),
  visitorPhone: phone.optional(),
  company: trimmed(1, 60).optional(),
  vehicleNo: trimmed(1, 20).optional(),
  purpose: trimmed(1, 120).optional(),
  idempotencyKey: z.string().min(8).max(80).optional(),
});

export const decideVisit = z.object({
  decision: z.enum(['approve', 'reject']),
  note: trimmed(1, 200).optional(),
});

export const enterVisit = z.object({
  /** Required (and admin-only) when the visitor was rejected. */
  overrideReason: trimmed(3, 200).optional(),
});

export const listVisitsQuery = paging.extend({
  status: visitStatus.optional(),
  unitId: uuid.optional(),
  /** Only visits that still need attention at the gate (pending / approved / no_response / entered). */
  open: z.enum(['true', 'false']).optional(),
});

export const visitListResponse = z.object({
  items: z.array(visitDto),
  nextCursor: z.string().nullable(),
});

// ───────────── Passes (pre-approvals) ─────────────

export const passDto = z.object({
  id: uuid,
  unitId: uuid,
  unitLabel: z.string(),
  kind: visitKind,
  visitorName: z.string(),
  visitorPhone: z.string().nullable(),
  vehicleNo: z.string().nullable(),
  code: z.string(),
  validFrom: isoDate,
  validUntil: isoDate,
  maxUses: z.number(),
  uses: z.number(),
  status: z.enum(['active', 'revoked', 'expired', 'used_up']),
  createdAt: isoDate,
});
export type PassDto = z.infer<typeof passDto>;

export const createPass = z
  .object({
    unitId: uuid,
    kind: visitKind.default('guest'),
    visitorName: trimmed(1, 80),
    visitorPhone: phone.optional(),
    vehicleNo: trimmed(1, 20).optional(),
    validFrom: z.iso.datetime({ offset: true }).optional(),
    validUntil: z.iso.datetime({ offset: true }),
    maxUses: z.number().int().min(1).max(50).default(1),
  })
  .refine((v) => new Date(v.validUntil).getTime() > Date.now(), {
    message: 'validUntil must be in the future',
    path: ['validUntil'],
  });

export const checkInPass = z.object({
  code: z.string().regex(/^\d{6}$/, 'Pass codes have 6 digits'),
  idempotencyKey: z.string().min(8).max(80).optional(),
});

/** Everything a guard device needs to work through a network drop. */
export const gateSnapshot = z.object({
  generatedAt: isoDate,
  units: z.array(
    z.object({
      id: uuid,
      label: z.string(),
      residents: z.array(z.object({ name: z.string().nullable(), phone: z.string() })),
    }),
  ),
  passes: z.array(
    z.object({
      id: uuid,
      code: z.string(),
      unitId: uuid,
      visitorName: z.string(),
      validFrom: isoDate,
      validUntil: isoDate,
      usesLeft: z.number(),
    }),
  ),
  settings: z.object({ approvalTimeoutSec: z.number(), noResponsePolicy: z.string() }),
});
